import { createContext, useContext, useMemo, type ReactNode } from 'react';

import type { RouterOutput } from '../../../../trpc/common';
import { trpc } from '../../../../trpc/react';

// Infer the exact shapes the org-world hooks expect, so the shared hooks can
// consume these without any narrowing/casting that would drop fields like
// `recordType` (present on the query output, absent on the domain `Metric`).
type AllMetricsByOrg =
  RouterOutput['admin']['workoutImport']['allMetricsByOrg'];
type BenchmarkGridsByOrg =
  RouterOutput['admin']['workoutImport']['benchmarkGridsByOrg'];
type DefaultBenchmarkGridByOrg =
  RouterOutput['admin']['workoutImport']['defaultBenchmarkGridByOrg'];

/**
 * Lets an admin page feed org-scoped metrics + benchmark grids into the shared
 * `useMetrics` / `useBenchmarks` hooks, which otherwise call orgProcedure /
 * teamProcedure endpoints that reject an admin session (401 "Not authorized as
 * an organization" → auth reset → redirect).
 *
 * The admin page renders the read-only `WorkoutTemplateSheet` inside this
 * provider; the hooks read from context instead of firing their org-scoped
 * queries. Outside this provider (e.g. the block-builder page) the context is
 * null and the hooks behave exactly as before.
 */
export interface OrgScopedMetricsValue {
  metrics: AllMetricsByOrg;
  benchmarkGrids: BenchmarkGridsByOrg;
  defaultBenchmarkGrid: DefaultBenchmarkGridByOrg;
  /** The selected org's default weight unit (from its OrgSettings), if loaded. */
  defaultWeightUnit?: string;
  /** True while any of the org-scoped queries is still fetching. */
  isLoading: boolean;
}

const OrgScopedMetricsContext = createContext<OrgScopedMetricsValue | null>(
  null,
);

export function useOrgScopedMetrics(): OrgScopedMetricsValue | null {
  return useContext(OrgScopedMetricsContext);
}

export const OrgScopedMetricsProvider = ({
  orgId,
  children,
}: {
  orgId: string;
  children: ReactNode;
}) => {
  const enabled = !!orgId;
  // Admin-scoped twins of metricsLibrary.allMetrics / benchmarks.fetchGrids /
  // benchmarks.fetchDefaultBenchmarkGrid — take orgId explicitly.
  // keepPreviousData + refetchOnWindowFocus: false — once a query has resolved,
  // later refetches (tab switches trigger the default focus refetch) must not
  // churn state: the embedded WorkoutTemplateSheet unmounts/resets its scroll
  // whenever metricsLoading flips back to true.
  const metricsQuery = trpc.admin.workoutImport.allMetricsByOrg.useQuery(
    { orgId },
    {
      enabled,
      staleTime: Infinity,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    },
  );
  const gridsQuery = trpc.admin.workoutImport.benchmarkGridsByOrg.useQuery(
    { orgId },
    {
      enabled,
      staleTime: Infinity,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    },
  );
  const defaultGridQuery =
    trpc.admin.workoutImport.defaultBenchmarkGridByOrg.useQuery(
      { orgId },
      {
        enabled,
        staleTime: Infinity,
        keepPreviousData: true,
        refetchOnWindowFocus: false,
      },
    );
  // The selected org's default weight unit — the admin twin of the org
  // settings the viewer exposes in the org world. useMetrics prefers this
  // while the provider is active (the teamSettings query it normally uses is
  // disabled for admin sessions).
  const orgSettingsQuery = trpc.admin.getOrgSettings.useQuery(
    { orgId },
    {
      enabled,
      staleTime: Infinity,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    },
  );

  const value = useMemo<OrgScopedMetricsValue>(
    () => ({
      metrics: metricsQuery.data ?? [],
      benchmarkGrids: gridsQuery.data ?? [],
      defaultBenchmarkGrid: defaultGridQuery.data,
      defaultWeightUnit: orgSettingsQuery.data?.defaultWeightUnit,
      isLoading:
        metricsQuery.isLoading ||
        gridsQuery.isLoading ||
        defaultGridQuery.isLoading,
    }),
    [
      metricsQuery.data,
      metricsQuery.isLoading,
      gridsQuery.data,
      gridsQuery.isLoading,
      defaultGridQuery.data,
      defaultGridQuery.isLoading,
      orgSettingsQuery.data,
    ],
  );

  return (
    <OrgScopedMetricsContext.Provider value={value}>
      {children}
    </OrgScopedMetricsContext.Provider>
  );
};
